import { FullStory } from '@fullstory/browser';
import * as Sentry from '@sentry/react';
import {
  Organization,
  Product,
  Team,
  UserRole,
} from '@shared/generated/graphql';
import { useFeatureFlagEnabled } from '@shared/hooks/useFeatureFlagEnabled';
import { IntegrationConfigs } from '@shared/types/order';
import { toName, User } from '@shared/types/user';
import { hasProducts } from '@shared/utils/permissions';
import { UserFeatures } from 'clerk_common';
import { FeatureFlagName } from 'clerk_common/types/featureFlags';
import { FrontProvider } from 'clerk_common/types/organization';
import { createContext, useEffect, useMemo } from 'react';
import { Me } from './hooks/useMe';

export function getUserFromMe(me: Me): User | undefined {
  if (!me) return undefined;

  return {
    id: me.id,
    userName: me.username ?? '',
    name: toName({ firstName: me.firstName, lastName: me.lastName }),
    email: me?.email ?? '',
    roles: me.roles,
    features: me.features as UserFeatures,
  };
}

type UseMeState = {
  me: Me | null;
  loading: boolean;
  refetch: () => void;
};

export type MeContextType = {
  me: Me | null;
  defaultOrgId: string | null;
  defaultOrgName: string | null;
  myTeams: Team[];
  loading: boolean;
  isVoomaAdmin: boolean;
  isVoomaOperations: boolean;
  hasQuotes: boolean;
  hasOrders: boolean;
  hasCalls: boolean;
  hasWorkers: boolean;
  hasEmailScanning: boolean;
  hasSchedule: boolean;
  hasCover: boolean;
  hasTrack: boolean;
  hasVee: boolean;
  frontEmailProvider: FrontProvider | null;
  frontApplicationWebhooksEnabled: boolean;
  integrationConfigs: IntegrationConfigs | null;
  refetch: () => void;
  hasProduct: (product: Product) => boolean;
};

export const MeContext = createContext<MeContextType>({
  loading: false,
  me: null,
  defaultOrgId: null,
  defaultOrgName: null,
  myTeams: [],
  isVoomaAdmin: false,
  isVoomaOperations: false,
  hasQuotes: false,
  hasOrders: false,
  hasCover: false,
  hasTrack: false,
  hasVee: false,
  hasCalls: false,
  hasWorkers: false,
  hasEmailScanning: false,
  hasSchedule: false,
  frontEmailProvider: null,
  frontApplicationWebhooksEnabled: false,
  integrationConfigs: null,
  refetch: () => null,
  hasProduct: () => false,
});

export const MeContextProvider = ({
  children,
  useMe,
  enableFullStory = true,
}: {
  children: JSX.Element;
  useMe: () => UseMeState;
  enableFullStory?: boolean;
}) => {
  const { me, loading, refetch } = useMe();

  const defaultOrgId = me?.organizationsDetails[0]?.id ?? null;
  const defaultOrgName = me?.organizationsDetails[0]?.name ?? null;
  const myTeams = (me?.teams as Team[]) ?? [];
  const integrationConfigs =
    me?.organizationsDetails[0]?.integrationConfigs ?? null;
  const frontEmailProvider =
    me?.organizationsDetails[0]?.settings?.front?.emailProvider ?? null;
  const frontApplicationWebhooksEnabled =
    me?.organizationsDetails[0]?.settings?.front?.applicationWebhooksEnabled ||
    false;

  const isVoomaAdmin = useMemo(() => {
    return Boolean(me?.roles.includes(UserRole.ADMIN));
  }, [me]);

  const isVoomaOperations = useMemo(() => {
    return Boolean(
      me?.roles.includes(UserRole.OPERATIONS) ||
        me?.roles.includes(UserRole.ADMIN)
    );
  }, [me]);

  const {
    hasQuotes,
    hasOrders,
    hasEmailScanning,
    hasWorkers,
    hasCalls,
    hasSchedule,
    hasCover,
    hasTrack,
    hasVee,
  } = useMemo(
    () => hasProducts((me?.organizationsDetails as Organization[]) ?? []),
    [me?.organizationsDetails]
  );

  const hasProduct = (product: Product) => {
    return Boolean(
      me?.organizationsDetails.find((org) => org.products.includes(product))
    );
  };

  const {
    isEnabled: hasBuildOrderButtonFlag,
    loading: hasBuildOrderButtonFlagLoading,
  } = useFeatureFlagEnabled({
    name: FeatureFlagName.BUILD_ORDER_BUTTON,
    userId: me?.id,
    organizationId: defaultOrgId,
  });

  const {
    isEnabled: hasBuildQuoteButtonFlag,
    loading: hasBuildQuoteButtonFlagLoading,
  } = useFeatureFlagEnabled({
    name: FeatureFlagName.BUILD_QUOTE_BUTTON,
    userId: me?.id,
    organizationId: defaultOrgId,
  });

  const {
    isEnabled: hasViewCallsButtonFlag,
    loading: hasViewCallsButtonFlagLoading,
  } = useFeatureFlagEnabled({
    name: FeatureFlagName.VIEW_CALLS_BUTTON,
    userId: me?.id,
    organizationId: defaultOrgId,
  });

  const hasBuildOrderButton = hasOrders && hasBuildOrderButtonFlag;
  const hasBuildQuoteButton = hasQuotes && hasBuildQuoteButtonFlag;
  const hasViewCallsButton = hasCalls && hasViewCallsButtonFlag;

  useEffect(() => {
    if (me?.id && me?.email) {
      Sentry.setUser({ id: me?.id, email: me?.email });
      enableFullStory &&
        FullStory('setIdentityAsync', { uid: me.id }).catch(() => {
          // no-op, most likely capture is disabled because we exceeded our quota
        });
    }
  }, [me?.id]);

  const isLoading =
    loading ||
    hasBuildOrderButtonFlagLoading ||
    hasBuildQuoteButtonFlagLoading ||
    hasViewCallsButtonFlagLoading;

  return (
    <MeContext.Provider
      value={{
        isVoomaAdmin,
        isVoomaOperations,
        me,
        defaultOrgId,
        defaultOrgName,
        myTeams,
        loading: isLoading,
        hasQuotes: hasBuildQuoteButton,
        hasOrders: hasBuildOrderButton,
        hasCalls: hasViewCallsButton,
        hasWorkers,
        hasEmailScanning,
        hasSchedule,
        hasCover,
        hasTrack,
        hasVee,
        frontEmailProvider,
        frontApplicationWebhooksEnabled,
        integrationConfigs,
        refetch,
        hasProduct,
      }}
    >
      {children}
    </MeContext.Provider>
  );
};
